import { Module } from '../../api/module';
import Identity from '../security/identity';

export type ModuleLicenses = Module['licenses'];

/**
 * What the user's license covers for a module, from the perspective of their role.
 */
export const MODULE_LICENSE = {
  FULL: 'FULL',
  DEMO: 'DEMO',
  /** Licensed teacher on a student module, a module only configured with student licenses. */
  STUDENT_LICENSE: 'STUDENT_LICENSE',
  TEACHER_NO_DOCUMENT: 'TEACHER_NO_DOCUMENT',
  TEACHER_NO_DIGITAL_MANUAL: 'TEACHER_NO_DIGITAL_MANUAL',
  STUDENT_NO_DOCUMENT: 'STUDENT_NO_DOCUMENT',
} as const;

export type ModuleLicense = keyof typeof MODULE_LICENSE;

/**
 * - HIDDEN: the feature is not meant for this user, a license would not change that (role, product rules).
 * - LOCKED: the feature is meant for this user but their license does not cover it, show it with a license alert.
 * - ALLOWED: the feature is fully usable.
 */
export const FEATURE_ACCESS = {
  HIDDEN: 'HIDDEN',
  LOCKED: 'LOCKED',
  ALLOWED: 'ALLOWED',
} as const;

export type FeatureAccess = keyof typeof FEATURE_ACCESS;

/**
 * `license` and `actsAs` are facts, the other fields are what the user may do with the module.
 * Being licensed does not imply access to every feature (e.g. a teacher using a student license).
 */
export interface ModuleAccess {
  license: ModuleLicense | undefined;
  actsAs: 'teacher' | 'student';
  canOpen: boolean;
  appsAccess: FeatureAccess;
  assignmentsAccess: FeatureAccess;
  resultsAccess: FeatureAccess;
  materialAccess: FeatureAccess;
  canShare: boolean;
  canCreatePresentation: boolean;
}

export interface ModuleAccessInput {
  module: Module | undefined;
  superModule: Module | undefined;
  identity: Identity;
  studioGroup: string | undefined;
}
